import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Kundenzugang from "./Kundenzugang";
import RatenListe from "../RatenListe";
import {
  aktuellerVertrag, anschrift, monatKurz, pflegeMonate, stufe, STUFE_LABEL, STUFE_TON,
} from "../../hooks/usePflegekunden";
import type { PflegeKunde } from "../../hooks/usePflegekunden";
import { useKundeObjekt, useKundenakte, datum, eur, VERTRAG_STATUS_LABEL } from "../../hooks/useVertraege";
import { datumDe, zahl } from "../../lib/zahl";

const PILL: Record<string, string> = {
  angeboten: "is-blau", zugestimmt: "is-gelb", aktiv: "is-gruen", gekuendigt: "is-rot",
};

/**
 * Ein Kunde aus Sicht der Pflege — gebaut wie Kontors Kundenansicht
 * (components/kunden/KundeAnsicht.tsx): Kopf mit Art und Nummer, Marken,
 * Wege zum Kunden, darunter Abschnitte mit Großbuchstaben-Titel.
 * Stammdaten ändert man in Kontor; hier steht, was die Pflege angeht.
 */
export default function KundeAnsicht({ k, onSchliessen }: { k: PflegeKunde; onSchliessen: () => void }) {
  const st = stufe(k);
  const aktuell = aktuellerVertrag(k);
  const monate = pflegeMonate(k);
  const { data: objekt } = useKundeObjekt(k.id);
  const { data: akte } = useKundenakte(k.id);
  const einsaetze = (akte?.vertraege || []).flatMap(v => v.einsaetze.map(e => ({ ...e, nummer: v.nummer })))
    .sort((a, b) => b.datum.localeCompare(a.datum)).slice(0, 6);
  const mitRaten = aktuell && !["entwurf", "angeboten"].includes(aktuell.status);
  const kontor = `https://kontor.vimaanagarten.de/kunden?kunde=${encodeURIComponent(k.id)}`;

  return (
    <article className="pk-kunde" aria-label={k.name}>
      <header className="pk-kunde-kopf">
        <div className="pk-kunde-titel">
          <div className="pf-eyebrow">{k.kundentyp === "firma" ? "Firmenkunde" : "Privatkunde"} · Nr. {k.id}</div>
          <h2>{k.name}</h2>
          {anschrift(k) && <div className="pk-kunde-anschrift">{anschrift(k)}</div>}
        </div>
        <button type="button" className="pf-btn is-ghost is-small pk-kunde-zu" onClick={onSchliessen}
          aria-label="Kunde schließen">✕</button>
      </header>

      <div className="pk-kunde-marken">
        <span className={"pf-pill " + STUFE_TON[st]}>{STUFE_LABEL[st]}</span>
        {monate.length > 0 && <span className="pf-pill is-gruen">Pflegekunde · {monate.length} {monate.length === 1 ? "Monat" : "Monate"}</span>}
        {aktuell?.monats_netto ? <span className="pf-pill">{eur(aktuell.monats_netto)} / Monat netto</span> : null}
      </div>

      <div className="pk-kunde-wege">
        {k.telefon ? <a className="pf-btn is-ghost" href={`tel:${k.telefon.replace(/[^\d+]/g, "")}`}>Anrufen</a>
          : <span className="pf-btn is-ghost is-aus" aria-disabled="true">Anrufen</span>}
        {k.email ? <a className="pf-btn is-ghost" href={`mailto:${k.email}`}>Mail</a>
          : <span className="pf-btn is-ghost is-aus" aria-disabled="true">Mail</span>}
        <a className="pf-btn is-ghost" href={kontor} target="_blank" rel="noreferrer">In Kontor öffnen ↗</a>
      </div>

      <Abschnitt titel="Verträge" rechts={
        <Link className={"pf-btn is-small" + (aktuell ? " is-ghost" : "")} to={`/vertraege/neu?kunde=${encodeURIComponent(k.id)}`}>
          {aktuell ? "＋ Weiterer Vertrag" : "＋ Vertrag anbieten"}
        </Link>}>
        {k.vertraege.length === 0 ? (
          <p className="pf-leer">Noch kein Vertrag. Die Pflegemonate aus Kontor sind der Ausgangspunkt.</p>
        ) : k.vertraege.map(v => (
          <Link key={v.id} className="pf-row" to={v.status === "entwurf" ? `/vertraege/neu?entwurf=${v.id}` : `/vertraege/${v.id}`}>
            <div className="pf-row-main">
              <div style={{ fontWeight: 600 }}>{v.status === "entwurf" ? "Entwurf" : v.nummer}{v.version > 1 ? ` · Fassung ${v.version}` : ""}</div>
              <div className="pf-klein">
                {v.beginn ? `ab ${datumDe(v.beginn)}` : "ohne Beginn"}
                {v.gekuendigt_zum ? ` · endet ${datum(v.gekuendigt_zum)}` : ""}
                {v.vertragsart === "unternehmer" ? " · Firmenvertrag" : ""}
              </div>
            </div>
            <div className="pf-row-side">
              <span>{v.monats_netto ? `${eur(v.monats_netto)} / Monat` : "—"}</span>
              <span className={"pf-pill " + (PILL[v.status] ?? "")}>{VERTRAG_STATUS_LABEL[v.status] ?? v.status}</span>
            </div>
          </Link>
        ))}
      </Abschnitt>

      {mitRaten && aktuell && (
        <Abschnitt titel={`Raten ${aktuell.nummer}`}>
          <RatenListe vertragId={aktuell.id} />
        </Abschnitt>
      )}

      <Abschnitt titel="Garten">
        {!objekt ? (
          <p className="pf-leer">Noch nicht aufgenommen — das geschieht im Vertrag, Schritt „Garten".</p>
        ) : (
          <>
            {(objekt.strasse || objekt.ort) && (
              <p className="pf-klein" style={{ marginBottom: 8 }}>
                {[objekt.strasse, [objekt.plz, objekt.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
              </p>
            )}
            {(objekt.positionen || []).length > 0 ? (
              <ul className="pf-fakten">
                {(objekt.positionen || []).map((p, i) => (
                  <li key={i}><span>{p.typ}</span><b>{zahl(p.groesse)} {p.einheit}</b>{p.zustand ? <em>{p.zustand}</em> : null}</li>
                ))}
              </ul>
            ) : <p className="pf-leer">Keine Flächen erfasst.</p>}
            {objekt.zugang_notiz && <p className="pf-klein" style={{ marginTop: 8 }}>Zugang: {objekt.zugang_notiz}</p>}
          </>
        )}
      </Abschnitt>

      <Abschnitt titel="Letzte Einsätze">
        {einsaetze.length === 0 ? <p className="pf-leer">Noch keine Einsätze in der Akte.</p> : (
          <ul className="pf-fakten">
            {einsaetze.map((e, i) => (
              <li key={i}><span>{datum(e.datum)}</span><b>{e.leistungen || "—"}</b><em>{e.status}</em></li>
            ))}
          </ul>
        )}
      </Abschnitt>

      <Abschnitt titel="Pflege im Jahr (laut Kontor)">
        {monate.length === 0 ? <p className="pf-leer">In Kontor sind keine Pflegemonate eingetragen.</p> : (
          <>
            <div className="pf-monatsband" aria-label="Pflegemonate">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <span key={m} className={monate.some(([x]) => x === m) ? "is-an" : ""}>{monatKurz(m)}</span>
              ))}
            </div>
            {monate.some(([, t]) => t) && (
              <p className="pf-klein" style={{ marginTop: 8 }}>
                {monate.filter(([, t]) => t).map(([m, t]) => `${monatKurz(m)} ${t}`).join(" · ")}
              </p>
            )}
          </>
        )}
      </Abschnitt>

      <Abschnitt titel="Kundenzugang" anker="zugang">
        <Kundenzugang kundeId={k.id} />
      </Abschnitt>
    </article>
  );
}

function Abschnitt({ titel, rechts, anker, children }: {
  titel: string; rechts?: ReactNode; anker?: string; children: ReactNode;
}) {
  return (
    <section className="pk-abschnitt" id={anker}>
      <div className="pk-abschnitt-kopf">
        <h3>{titel}</h3>
        {rechts}
      </div>
      {children}
    </section>
  );
}
